// ./src/contexts/GoogleMapsContext/GoogleMapsContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

// ============================================
// Types
// ============================================

export interface GoogleMapsContextValue {
  isLoaded: boolean;
  isLoading: boolean;
  error: Error | null;
  loadLibraries: <T extends string>(libraries: T[]) => Promise<Record<T, any>>;
  getCoreLibrary: () => typeof google.maps | null;
  getLibrary: <T = any>(name: string) => T | null;
}

export interface GoogleMapsProviderOptions {
  libraries?: string[];
}

export interface GoogleMapsProviderProps {
  children: React.ReactNode;
  options?: GoogleMapsProviderOptions;
}

// ============================================
// Context
// ============================================

const defaultContextValue: GoogleMapsContextValue = {
  isLoaded: false,
  isLoading: false,
  error: null,
  loadLibraries: async () => {
    throw new Error("GoogleMapsProvider not initialized");
  },
  getCoreLibrary: () => null,
  getLibrary: () => null,
};

const GoogleMapsContext =
  createContext<GoogleMapsContextValue>(defaultContextValue);

// ============================================
// Provider
// ============================================

export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({
  children,
  options = {},
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadedLibrariesRef = useRef<Map<string, any>>(new Map());
  const hasAutoLoadedRef = useRef(false);
  const hasInitializedRef = useRef(false);

  const initializeOptions = useCallback(() => {
    if (!hasInitializedRef.current) {
      setOptions({
        key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
        v: "weekly",
      });
      hasInitializedRef.current = true;
    }
  }, []);

  const getCoreLibrary = useCallback(() => {
    return typeof google !== "undefined" && google.maps ? google.maps : null;
  }, []);

  /**
   * Get a specific loaded library by name
   */
  const getLibrary = useCallback(<T = any>(name: string): T | null => {
    return loadedLibrariesRef.current.get(name) as T | null;
  }, []);

  const loadLibraries = useCallback(
    async <T extends string>(libraryNames: T[]): Promise<Record<T, any>> => {
      try {
        initializeOptions();

        setIsLoading(true);
        setError(null);

        const result: Record<string, any> = {};

        console.log("[GoogleMapsContext] Loading libraries:", libraryNames);

        // Load libraries
        for (const lib of libraryNames) {
          // Check if already loaded
          if (loadedLibrariesRef.current.has(lib)) {
            console.log(`[GoogleMapsContext] Library "${lib}" already loaded`);
            result[lib] = loadedLibrariesRef.current.get(lib);
          } else {
            console.log(`[GoogleMapsContext] Loading library "${lib}"...`);
            const loadedLib = await importLibrary(lib as any);
            console.log(`[GoogleMapsContext] Library "${lib}" loaded:`, loadedLib);
            loadedLibrariesRef.current.set(lib, loadedLib);
            result[lib] = loadedLib;
          }
        }

        console.log("[GoogleMapsContext] All libraries loaded:", result);

        // Set loaded flag if not already set
        if (!isLoaded) {
          setIsLoaded(true);
        }

        return result as Record<T, any>;
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Failed to load Google Maps");
        setError(error);
        console.error("[GoogleMapsContext] Error loading Google Maps:", error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [isLoaded, initializeOptions]
  );

  // Autoload libraries on mount if specified in options
  useEffect(() => {
    if (
      options.libraries &&
      options.libraries.length > 0 &&
      !hasAutoLoadedRef.current
    ) {
      hasAutoLoadedRef.current = true;
      loadLibraries(options.libraries);
    }
  }, [options.libraries, loadLibraries]);

  const value: GoogleMapsContextValue = {
    isLoaded,
    isLoading,
    error,
    loadLibraries,
    getCoreLibrary,
    getLibrary,
  };

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Basic hook for accessing Google Maps context
 */
export const useGoogleMapsContext = (): GoogleMapsContextValue => {
  const context = useContext(GoogleMapsContext);
  return context;
};

/**
 * Hook for loading specific Google Maps libraries
 * Returns the loaded libraries along with loading state
 *
 * @example
 * const { libraries, isLoaded, isLoading, error } = useGoogleMapsLibraries(['maps', 'marker']);
 * if (isLoaded) {
 *   const { Map } = libraries.maps;
 *   const { AdvancedMarkerElement } = libraries.marker;
 * }
 */
export interface UseGoogleMapsLibrariesResult<T extends string> {
  libraries: Record<T, any>;
  isLoaded: boolean;
  isLoading: boolean;
  error: Error | null;
}

export function useGoogleMapsLibraries<T extends string>(
  libraryNames: T[]
): UseGoogleMapsLibrariesResult<T> {
  const { loadLibraries, getLibrary, isLoaded: contextLoaded } = useGoogleMapsContext();
  const [libraries, setLibraries] = useState<Record<T, any>>({} as Record<T, any>);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const loadAttemptedRef = useRef(false);

  // Stringify library names to use as stable dependency
  const libraryNamesKey = libraryNames.join(",");

  useEffect(() => {
    console.log("[useGoogleMapsLibraries] Effect running, loadAttempted:", loadAttemptedRef.current);

    // Skip if already attempted or no libraries to load
    if (loadAttemptedRef.current || libraryNames.length === 0) {
      console.log("[useGoogleMapsLibraries] Skipping - already attempted or no libraries");
      return;
    }

    // Check if all libraries are already loaded (use != null to check both null and undefined)
    const allLoaded = libraryNames.every((name) => {
      const lib = getLibrary(name);
      return lib != null; // checks for both null and undefined
    });

    if (allLoaded) {
      console.log("[useGoogleMapsLibraries] All libraries already loaded in context");
      const loadedLibs = {} as Record<T, any>;
      libraryNames.forEach((name) => {
        loadedLibs[name] = getLibrary(name);
      });
      setLibraries(loadedLibs);
      setIsLoaded(true);
      return;
    }

    // Load libraries
    console.log("[useGoogleMapsLibraries] Starting to load libraries:", libraryNames);
    loadAttemptedRef.current = true;
    setIsLoading(true);

    loadLibraries(libraryNames)
      .then((result) => {
        console.log("[useGoogleMapsLibraries] loadLibraries resolved with:", result);
        setLibraries(result);
        setIsLoaded(true);
        setError(null);
      })
      .catch((err) => {
        console.error("[useGoogleMapsLibraries] loadLibraries failed:", err);
        setError(err instanceof Error ? err : new Error("Failed to load libraries"));
        setIsLoaded(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryNamesKey, loadLibraries, getLibrary, contextLoaded]);

  return { libraries, isLoaded, isLoading, error };
}

/**
 * Hook specifically for map display components
 * Loads 'maps' and 'marker' libraries needed for displaying maps with markers
 */
export interface GoogleMapsDisplayLibraries {
  maps: typeof google.maps;
  marker: typeof google.maps.marker;
}

export interface UseGoogleMapsDisplayResult {
  libraries: GoogleMapsDisplayLibraries | null;
  isLoaded: boolean;
  isLoading: boolean;
  error: Error | null;
}

// Constant array to avoid creating new reference on each render
const DISPLAY_LIBRARY_NAMES = ["maps", "marker"] as const;

export function useGoogleMapsDisplay(): UseGoogleMapsDisplayResult {
  const { libraries, isLoaded, isLoading, error } = useGoogleMapsLibraries(
    DISPLAY_LIBRARY_NAMES as unknown as string[]
  );

  return {
    libraries: isLoaded ? (libraries as GoogleMapsDisplayLibraries) : null,
    isLoaded,
    isLoading,
    error,
  };
}